/**
 * The Rat Swarm event core: rat entities + the pure, seeded, clock-free brain
 * of the whole event (PRD #222, Slice C).
 *
 * Fifty giant rats flood the bank, skitter around for up to ninety seconds, and
 * anyone present stomps them for gold; the event ends at the first of extinction
 * or timeout. This module owns every **decision** - where the rats spawn, when
 * and where each one skitters, whether a stomp kills, when the event ends, and
 * who won - exactly the way `npc.ts` owns barking and ambling: pure functions of
 * `(world, tickCount, state)` that read no wall clock and draw all randomness
 * from the world's seeded `rng`. The **acts** (receiving `Stomp` commands,
 * crediting durable gold, broadcasting the finale) are server I/O and live in
 * `server/` (Slice D wires them); the server passes tick counts and opaque
 * session keys in, so nothing here ever touches a clock or a socket.
 *
 * Rats are ordinary world entities - `position` + `mobile` + `renderable`
 * carrying the giant-rat body + the `rat` marker (`entity.ts`) - so they ride
 * the existing snapshot/delta stream, render, and interpolate with zero new
 * network or rendering paths, and persistence carries the marker additively
 * (no schema bump; the dice precedent). They deliberately do **not** participate
 * in walkability (occupancy is doors-only, `walkability.ts`), so the swarm can
 * never block a player.
 *
 * Event state ({@link RatSwarmState}) is deliberately **ephemeral server
 * memory**, not world state: an event is a non-deterministic operator action,
 * its tallies key off connection sessions, and nothing about it should survive
 * a restart - so it lives beside the world, never in a snapshot.
 *
 * See ADR-0117.
 */
import type { Entity, EntityId } from './entity';
import type { World } from './world';
import type { WanderPlan, WanderTiming, Wanderer } from './wander';
import { Mobile } from './mobile';
import { ALL_DIRECTIONS, type Direction } from './direction';
import { doorAwareWalkable } from './walkability';
import { GIANT_RAT_BODY } from './content';
import { MAP_PADDING, BANK_BLOCK_WIDTH, BANK_BLOCK_HEIGHT } from './mapLayout';

// ---------------------------------------------------------------------------
// Tunables. Every knob of the event, in one place (PRD #222's starting values).
// ---------------------------------------------------------------------------

/** How many rats a swarm spawns. */
export const RAT_COUNT = 50;

/** Max Chebyshev distance (the 8-directional metric) a stomp reaches. */
export const STOMP_RANGE = 2;

/** Gold credited for every rat killed. */
export const GOLD_PER_RAT = 10;

/** Extra gold credited on top of {@link GOLD_PER_RAT} for the final rat. */
export const FINAL_RAT_BONUS = 50;

/**
 * Event cap in **ticks**: ninety seconds' worth of the server's 50ms fixed steps
 * (`STEP_MS`, `server/gameServer.ts`). Expressed as ticks - never milliseconds -
 * because the sim is clock-free: the server feeds {@link stepRatSwarm} one call
 * per tick and the cap falls out of counting, exactly like NPC cadences.
 */
export const SWARM_DURATION_TICKS = 1800;

// ---------------------------------------------------------------------------
// Rat entities + seeded spawn.
// ---------------------------------------------------------------------------

/**
 * A half-open tile rectangle `[x0, x1) x [y0, y1)` in the map's absolute frame:
 * the area a swarm spawns into and skitters within.
 */
export interface RatRegion {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * The default swarm region: the original bank block, in the extended map's
 * absolute frame (the `MAP_PADDING`-offset rectangle `mapLayout.ts` defines).
 * "Interior" resolves per tile through the walkable mask: the block's cells are
 * only walkable where the bank's floors (and the ADR-0005 entrance carve) are,
 * so intersecting the block rectangle with `doorAwareWalkable` lands rats on
 * real bank floor - the street padding around the block and the roof plate
 * (a separate z-level surface) are excluded by construction. The playable
 * extension south of the block is deliberately outside too: the swarm floods
 * the *bank*, not the lawn.
 */
export const BANK_INTERIOR: RatRegion = {
  x0: MAP_PADDING,
  y0: MAP_PADDING,
  x1: MAP_PADDING + BANK_BLOCK_WIDTH,
  y1: MAP_PADDING + BANK_BLOCK_HEIGHT,
};

function inRegion(region: RatRegion, x: number, y: number): boolean {
  return x >= region.x0 && x < region.x1 && y >= region.y0 && y < region.y1;
}

/** Chebyshev distance: the natural range metric for 8-directional movement. */
function chebyshev(ax: number, ay: number, bx: number, by: number): number {
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}

/**
 * Create one giant rat and register it: an ordinary mobile entity (`position` +
 * a live {@link Mobile} + `renderable` with the giant-rat body) plus the `rat`
 * marker the server validates stomps against. No name label (rats are vermin,
 * not people) and default gait; both are tunable later without touching the
 * event rules.
 */
export function createRat(
  world: World,
  spec: { x: number; y: number; facing: Direction },
): Entity {
  return world.createEntity({
    position: { x: spec.x, y: spec.y, facing: spec.facing },
    mobile: new Mobile({ x: spec.x, y: spec.y, facing: spec.facing }),
    renderable: { body: GIANT_RAT_BODY },
    rat: true,
    // The replicated interaction role (D5): a rat is the grey war-mode target.
    interaction: { role: 'war-target' },
  });
}

/**
 * Seeded placement of a swarm: `count` rats on walkable tiles inside `region`.
 *
 * The candidate set is every region cell the live door-aware mask allows
 * (closed entrance doors excluded, so no rat materialises inside a shut door),
 * collected in row-major order so it is a pure function of the world. Placement
 * draws from `world.rng` - the sanctioned sim randomness, whose state rides
 * every snapshot, so a client read-model can never desync - via a partial
 * Fisher-Yates: rats land on **distinct** tiles while distinct tiles remain
 * (a spread-out swarm, not a stack), then wrap to seeded re-picks when the
 * region is smaller than the swarm (legal: rats are walk-through). Each rat
 * also draws a seeded facing. Throws when the region has no walkable cell -
 * a mis-authored region should fail loudly at start, never spawn zero rats
 * silently.
 */
export function spawnRats(
  world: World,
  opts: { count?: number; region?: RatRegion } = {},
): EntityId[] {
  const count = opts.count ?? RAT_COUNT;
  const region = opts.region ?? BANK_INTERIOR;
  const isWalkable = doorAwareWalkable(world);

  const candidates: { x: number; y: number }[] = [];
  for (let y = region.y0; y < region.y1; y++) {
    for (let x = region.x0; x < region.x1; x++) {
      if (isWalkable(x, y)) candidates.push({ x, y });
    }
  }
  if (candidates.length === 0) {
    throw new Error('spawnRats: region contains no walkable interior tile');
  }

  const ids: EntityId[] = [];
  for (let i = 0; i < count; i++) {
    let cell: { x: number; y: number };
    if (i < candidates.length) {
      // Partial Fisher-Yates: draw the i-th distinct tile.
      const j = i + world.rng.nextInt(candidates.length - i);
      const tmp = candidates[i]!;
      candidates[i] = candidates[j]!;
      candidates[j] = tmp;
      cell = candidates[i]!;
    } else {
      // More rats than tiles: overlap on seeded re-picks.
      cell = candidates[world.rng.nextInt(candidates.length)]!;
    }
    const facing = ALL_DIRECTIONS[world.rng.nextInt(ALL_DIRECTIONS.length)]!;
    ids.push(createRat(world, { x: cell.x, y: cell.y, facing }).id);
  }
  return ids;
}

// ---------------------------------------------------------------------------
// Seeded skitter (the rat wander pass).
// ---------------------------------------------------------------------------

/**
 * The rats' own timing salt, distinct from the NPC speech (`0x9e3779b1`) and
 * NPC wander (`0x85ebca6b`) salts, so the swarm's cadence stream never
 * correlates with the bank-sitters'. A murmur3 spreading constant; purely a
 * quality-of-spread value, not a secret.
 */
const RAT_WANDER_TIMING_SALT = 0xc2b2ae35;

/**
 * Base ticks a parked rat rests between skitter legs (20 ticks = 1s at the 50ms
 * step). Deliberately much shorter than the NPCs' lazy 3s amble - a swarm
 * should boil, not mill about.
 */
const RAT_WANDER_INTERVAL_TICKS = 20;

/** Upper bound on the seeded per-rat spread folded into the interval. */
const RAT_WANDER_JITTER_TICKS = 20;

/**
 * The rats' cadence constants for the shared {@link ./wander.stepWander} brain
 * (ADR-0261): per-rat period/phase derive from the **entity id** alone (rats
 * have no roster; ids are stable for the world's lifetime) - never the wall
 * clock, never the live `world.rng` - so timing is stable tick-to-tick with no
 * scheduler state, and seed-independent exactly like the NPC cadences: the
 * world seed drives only *where* a rat goes, not *when*.
 */
export const RAT_WANDER_TIMING: WanderTiming = {
  salt: RAT_WANDER_TIMING_SALT,
  intervalTicks: RAT_WANDER_INTERVAL_TICKS,
  jitterTicks: RAT_WANDER_JITTER_TICKS,
};

/**
 * The swarm's contribution to the ONE shared wander brain
 * ({@link ./wander.stepWander}, Chassis Slice B): the world's `rat`-tagged
 * entities, bounded by the swarm `region` intersected with the live door-aware
 * mask - the region plays the role an NPC's home radius plays, keeping the
 * swarm boiling inside the bank rather than dispersing out the doors, without
 * any per-rat home state.
 *
 * When a `live` set is supplied (the server passes `RatSwarmState.rats`), only
 * the rats in it skitter: a killed rat's corpse lingers in the world for a few
 * ticks while its death clip plays (ADR-0119), and a walking corpse would read
 * as a resurrection.
 */
export function ratWanderPlan(
  region: RatRegion = BANK_INTERIOR,
  live?: ReadonlySet<EntityId>,
): WanderPlan {
  return {
    timing: RAT_WANDER_TIMING,
    wanderers: (world: World) => {
      const isWalkable = doorAwareWalkable(world);
      const legal = (x: number, y: number): boolean => inRegion(region, x, y) && isWalkable(x, y);
      const out: Wanderer[] = [];
      for (const entity of world.all()) {
        if (!entity.rat) continue;
        if (live && !live.has(entity.id)) continue;
        out.push({ entity: entity.id, legal });
      }
      return out;
    },
  };
}

// ---------------------------------------------------------------------------
// The pure event core: state, stomp resolution, end conditions, winner.
// ---------------------------------------------------------------------------

/**
 * One participant's running score. Keyed in {@link RatSwarmState.tallies} by an
 * **opaque session key** the server supplies (a connection/session id), so
 * reserved and anonymous hunters are counted alike for the live board; `name`
 * is the display name to show for that key (refreshed on every kill, so a
 * renamed session reads current).
 */
export interface StomperTally {
  /** Display name for the event board. */
  name: string;
  /** Rats this session has stomped this event. */
  kills: number;
  /**
   * The event-wide kill sequence number at which this tally last grew - i.e.
   * when this stomper *reached* its current count. The winner tiebreak: of two
   * equal counts, the one reached first (smaller value) wins.
   */
  reachedAtSeq: number;
}

/** How a swarm is (or stopped) running. */
export type SwarmStatus = 'active' | 'extinct' | 'timedOut';

/**
 * The whole event's state: live rat ids, per-session tallies, and the tick
 * clock - plain ephemeral server memory (never persisted, never on a snapshot;
 * see the module header). Mutated only by {@link resolveStomp} and
 * {@link stepRatSwarm}, both clock-free.
 */
export interface RatSwarmState {
  status: SwarmStatus;
  /**
   * The region this swarm spawned into and skitters within, carried on the
   * event state so spawn and wander can never disagree about the bounds
   * (the server passes it back into {@link ratWanderPlan} each tick).
   */
  region: RatRegion;
  /**
   * Ids of the rats still **alive** - spawned, not yet killed or scattered.
   * Membership here is what "stompable" and "survivor" mean: a killed rat
   * leaves this set on the kill tick even though its corpse entity may linger
   * in the world a few more ticks while the death clip plays (ADR-0119), so a
   * stomp on a dying rat is the silent race-lost no-op and a corpse never
   * counts toward extinction or the timeout scatter.
   */
  rats: Set<EntityId>;
  /** Per-session scores, keyed by the server's opaque session key. */
  tallies: Map<string, StomperTally>;
  /** Ticks elapsed since the event started (advanced by {@link stepRatSwarm}). */
  elapsedTicks: number;
  /** Monotonic count of kills so far; feeds {@link StomperTally.reachedAtSeq}. */
  killSeq: number;
  /** Who landed the final blow, once someone has; `null` = unclaimed. */
  finalBlow: { session: string; name: string } | null;
}

/**
 * Start a swarm: seeded-spawn the rats ({@link spawnRats}) and return the fresh
 * event state tracking them. The single sim-side entrypoint the server's
 * operator trigger (Slice D) calls; refusing a second concurrent event is the
 * server's job (it owns "is one running"), not the core's.
 */
export function startRatSwarm(
  world: World,
  opts: { count?: number; region?: RatRegion } = {},
): RatSwarmState {
  const region = opts.region ?? BANK_INTERIOR;
  return {
    status: 'active',
    region,
    rats: new Set(spawnRats(world, { ...opts, region })),
    tallies: new Map(),
    elapsedTicks: 0,
    killSeq: 0,
    finalBlow: null,
  };
}

/**
 * One stomp as the server hands it to the core: who (opaque session key +
 * display name), from where (the stomper's **server-authoritative** tile, never
 * a client claim), in what stance, at which target.
 */
export interface StompAttempt {
  /** Opaque per-connection session key (reserved and anonymous alike). */
  session: string;
  /** The stomper's display name, for the tally/board. */
  name: string;
  /** Whether the stomper is in war mode (client-local stance, server-passed). */
  warMode: boolean;
  /** The stomper's authoritative tile. */
  x: number;
  y: number;
  /** The rat entity the stomp names. */
  target: EntityId;
}

/**
 * What a stomp resolved to. `ignored` is the **silent no-op** of the one-kill-
 * per-rat rule: the target was already removed (the server processes commands
 * serially, so first-come-at-the-server is the whole tiebreak, and the loser of
 * a race hears nothing rather than an error). `rejected` is an invalid attempt
 * - inactive event, target not a rat, no war mode, out of range - the server
 * may surface or drop as it sees fit.
 */
export type StompOutcome =
  | { kind: 'killed'; gold: number; finalBlow: boolean }
  | { kind: 'ignored' }
  | { kind: 'rejected'; reason: 'inactive' | 'not-a-rat' | 'no-war-mode' | 'out-of-range' };

/**
 * Resolve one stomp against the live world + event state. A valid stomp -
 * active event, target exists and carries the `rat` marker, stomper in war
 * mode, stomper's tile within {@link STOMP_RANGE} (Chebyshev) of the rat -
 * kills the rat: it leaves the live set, its wander halts, the session's tally
 * bumps, and the gold to credit is reported: {@link GOLD_PER_RAT}, plus
 * {@link FINAL_RAT_BONUS} when this was the last rat. Killing the last rat is
 * also what ends the event (**extinction, immediately**) and records the
 * final blow for the finale announcement.
 *
 * The kill is final on this call, but the corpse **entity is deliberately not
 * removed here** (ADR-0119): the caller owns the despawn and defers it a few
 * ticks so the broadcast `die` clip can play out on clients before the delta
 * `removed` culls the sprite. One-kill-per-rat still holds - live-set
 * membership, not world presence, is what "stompable" means, so a second stomp
 * on the dying corpse is the same silent `ignored` as one on a vanished
 * entity. Crediting durable gold, broadcasting, and the deferred removal are
 * the server's acts (Slice D); this only decides.
 */
export function resolveStomp(
  world: World,
  state: RatSwarmState,
  attempt: StompAttempt,
): StompOutcome {
  if (state.status !== 'active') return { kind: 'rejected', reason: 'inactive' };

  const target = world.get(attempt.target);
  if (!target || !state.rats.has(attempt.target)) {
    // A live non-rat entity is a bad target; a missing one - or a **dying**
    // rat (killed this event, corpse still despawning, so marked but no longer
    // in the live set) - is the one-kill-per-rat race lost: silence, not an
    // error.
    if (target && !target.rat) return { kind: 'rejected', reason: 'not-a-rat' };
    return { kind: 'ignored' };
  }
  if (!target.rat) return { kind: 'rejected', reason: 'not-a-rat' };
  if (!attempt.warMode) return { kind: 'rejected', reason: 'no-war-mode' };

  const pos = target.position;
  if (!pos || chebyshev(attempt.x, attempt.y, pos.x, pos.y) > STOMP_RANGE) {
    return { kind: 'rejected', reason: 'out-of-range' };
  }

  // The kill: one per rat, guaranteed by the live-set delete (a later stomp
  // finds the target outside the set and is silently ignored). The corpse
  // entity stays in the world for the caller's deferred despawn (ADR-0119);
  // clearing its intent parks it on the next tile boundary so the death clip
  // plays over a stationary corpse, and the live-set filter in
  // the wander pass ({@link ratWanderPlan}) keeps it from ever setting off again.
  state.rats.delete(attempt.target);
  target.mobile?.setIntent(null);
  state.killSeq += 1;

  const tally = state.tallies.get(attempt.session);
  if (tally) {
    tally.name = attempt.name;
    tally.kills += 1;
    tally.reachedAtSeq = state.killSeq;
  } else {
    state.tallies.set(attempt.session, {
      name: attempt.name,
      kills: 1,
      reachedAtSeq: state.killSeq,
    });
  }

  const finalBlow = state.rats.size === 0;
  if (finalBlow) {
    state.status = 'extinct';
    state.finalBlow = { session: attempt.session, name: attempt.name };
  }
  return {
    kind: 'killed',
    gold: GOLD_PER_RAT + (finalBlow ? FINAL_RAT_BONUS : 0),
    finalBlow,
  };
}

/**
 * Advance the event clock by one tick and apply the timeout end condition.
 * Called once per server tick while an event runs; a no-op after the event has
 * ended (extinction already stopped the clock in {@link resolveStomp}).
 *
 * On the {@link SWARM_DURATION_TICKS}-th tick the survivors **scatter**: every
 * live rat is removed from the world (the despawn deltas ride the existing
 * stream; any scatter flourish is a client concern), the live set empties, and
 * the event is `timedOut` with the final-blow bonus left **unclaimed**
 * (`finalBlow` stays `null`) - clearing all fifty is a real collective goal
 * with a real fail state. Returns what ended (if anything) and which rats
 * despawned, so the server can announce and clean up.
 */
export function stepRatSwarm(
  world: World,
  state: RatSwarmState,
): { ended: 'timedOut' | null; despawned: EntityId[] } {
  if (state.status !== 'active') return { ended: null, despawned: [] };

  state.elapsedTicks += 1;
  if (state.elapsedTicks < SWARM_DURATION_TICKS) return { ended: null, despawned: [] };

  const despawned = [...state.rats];
  for (const id of despawned) world.remove(id);
  state.rats.clear();
  state.status = 'timedOut';
  return { ended: 'timedOut', despawned };
}

/**
 * The event's winner: the top stomper across the whole event, ties broken in
 * favour of whoever **reached** the top count first (smaller `reachedAtSeq` -
 * fully deterministic, no RNG draw). `null` when nobody killed anything.
 * Selection only; announcing is the server's act.
 */
export function swarmWinner(
  state: RatSwarmState,
): { session: string; name: string; kills: number } | null {
  let winner: { session: string; name: string; kills: number } | null = null;
  let winnerSeq = Infinity;
  for (const [session, tally] of state.tallies) {
    if (
      winner === null ||
      tally.kills > winner.kills ||
      (tally.kills === winner.kills && tally.reachedAtSeq < winnerSeq)
    ) {
      winner = { session, name: tally.name, kills: tally.kills };
      winnerSeq = tally.reachedAtSeq;
    }
  }
  return winner;
}
